import { useFirestoreGeneral, useFirestoreOrderBy } from '../../../firebase/useFirestore'
import useSettings from '../../../hooks/Settings.jsx'
import { sectorQualifyingQuestions, qualifyingSetsFor } from '../../../helpers/questionnaire/sectorQualifyingQuestions'
import { qualifyingAnswerFor } from '../../../helpers/questionnaire/qualifyingAnswerFor'

/**
 * De kaart 'Kwalificering' op het tabblad Gegevens van een huishouden: de
 * antwoorden op de kwalificeringsvragen, zoals de server ze vanuit de
 * vragenlijst op het huishouden zette. Alleen lezen — aanpassen gaat door de
 * vragenlijst opnieuw in te vullen.
 *
 * Getoond voor elke set van de sectoren van de organisatie, plus elke set
 * waarvoor het huishouden al antwoorden heeft (een sector kan later uitgezet
 * zijn; het antwoord blijft waar). Tijdens het laden niets: 'Nog niet
 * beantwoord' is een bewering die pas klopt als we het huishouden kennen.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 * AI-CONTEXT: See docs/features/kwalificering-op-huishouden.md
 *
 * @param {object} props
 * @param {string} props.adressID - Adresses.ID van het huishouden
 * @returns {JSX.Element|null}
 */
const HouseholdQualifyingCard = ({ adressID }) => {
    const { loaded, selectedSectors } = useSettings()
    const adressList = useFirestoreGeneral('Adresses', 'ID', adressID || '')
    // Dezelfde vraag als Research.jsx en ResearchCard.jsx op deze pagina: de SDK
    // deelt dan één listener, in plaats van alle onderzoeken opnieuw te lezen.
    const researches = useFirestoreOrderBy('Research', 'Position', 'asc')

    const household = Array.isArray(adressList) && adressList.length > 0 ? adressList[0] : null
    if (!loaded || !household) return null

    const answeredSets = Object.keys(household.Qualifying || {}).filter((slug) => sectorQualifyingQuestions[slug])
    const sets = qualifyingSetsFor([...(selectedSectors || []), ...answeredSets])
    if (sets.length === 0) return null

    /** @param {string} researchID */
    const researchTitle = (researchID) => {
        const research = Array.isArray(researches) ? researches.find((r) => r.ID === researchID) : null
        return research ? research.Title : ''
    }

    return (
        <div className="dashboard-container adress-household-qualifying">
            <h3>Kwalificering</h3>
            {sets.map((set) => (
                <div key={set.key} className="household-qualifying-set">
                    {set.questions.map((question) => {
                        const answer = qualifyingAnswerFor(household, question.key)
                        const option = answer && question.options.find((o) => o.key === answer.Answer)
                        // Een antwoord dat bij geen optie past (een sleutel die later
                        // veranderde) is wél gegeven: toon wat er staat.
                        const label = option ? option.label : (answer && answer.Answer) || ''
                        const date = answer && answer.AnsweredAt ? answer.AnsweredAt.toDate().toLocaleDateString('nl-NL') : ''
                        const title = answer ? researchTitle(answer.ResearchID) : ''
                        return (
                            <div key={question.key} className="household-qualifying-row">
                                <span className="household-qualifying-question">{question.question}</span>
                                {label ? (
                                    <span className="household-qualifying-answer">
                                        <strong>{label}</strong>
                                        <span className="household-qualifying-meta">
                                            {[date, title].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="household-qualifying-answer household-qualifying-answer--empty">Nog niet beantwoord</span>
                                )}
                            </div>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}

export default HouseholdQualifyingCard
